import React, { useMemo } from 'react';

import { cn } from '@/lib/utils';
import { getBadgeStyles, getLevelProgress } from '@/lib/badgeMetrics';

import './UserAvatar.css';

interface UserAvatarProps {
  name?: string;
  level: number;
  lifetimePoints?: number;
  src?: string | null;
  size?: number;
  showChip?: boolean;
  className?: string;
  /** Override the inner circle background (e.g. a gradient placeholder). */
  innerStyle?: React.CSSProperties;
  /** Extra classes for the initials (e.g. text colour on a coloured inner). */
  initialsClassName?: string;
}

const initialsOf = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Avatar wrapped by a tier-coloured badge ring (doubling as a level-progress
 * arc) with a level chip. Used in leaderboards, profile, and review queues.
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  level,
  lifetimePoints,
  src,
  size = 48,
  showChip = true,
  className,
  innerStyle,
  initialsClassName,
}) => {
  const badge = useMemo(() => getBadgeStyles(level), [level]);
  const progress =
    lifetimePoints === undefined ? 1 : getLevelProgress(lifetimePoints);

  const ringWidth = Math.max(3, Math.round(size * 0.08));
  const progressPct = Math.round(progress * 100);
  const track = 'hsl(var(--muted))';

  const chipSize = Math.max(14, Math.round(size * 0.34));
  const chipFont = Math.max(8, Math.round(size * 0.2));

  return (
    <div
      className={cn('corpus-user-avatar', className)}
      style={{ width: size, height: size }}
      aria-label={
        name ? `${name}, level ${badge.level}` : `Level ${badge.level}`
      }
    >
      <div
        className="corpus-user-avatar__ring"
        style={
          {
            padding: ringWidth,
            boxShadow: badge.glow || undefined,
            '--avatar-c-light': badge.colorLight,
            '--avatar-c-deep': badge.colorDeep,
            '--avatar-track': track,
            '--avatar-progress': `${progressPct}%`,
          } as React.CSSProperties
        }
      >
        <div className="corpus-user-avatar__inner" style={innerStyle}>
          {src ? (
            <img src={src} alt={name ?? 'avatar'} />
          ) : (
            <span
              className={cn('corpus-user-avatar__initials', initialsClassName)}
              style={{ fontSize: Math.max(10, Math.round(size * 0.32)) }}
            >
              {initialsOf(name)}
            </span>
          )}
        </div>
        {badge.hasSheen && (
          <span className="corpus-user-avatar__sheen" aria-hidden="true" />
        )}
      </div>

      {showChip && (
        <span
          className="corpus-user-avatar__chip"
          style={{
            minWidth: chipSize,
            height: chipSize,
            fontSize: chipFont,
            background: badge.chipGradient,
          }}
          title={`${badge.tierName} · Level ${badge.level}`}
        >
          {badge.level}
        </span>
      )}
    </div>
  );
};

export default UserAvatar;
